import { PxwConnectorFormData, ConnectorConfig } from './types';
import {
  buildVanillaSettingsPayload,
  buildQboConfiguratorPayload,
  buildQboSetupPayload,
  buildShipStationPayload,
  buildDiagnosticPayload,
} from './payloads';

export const getGatewayBaseUrlByEnv = (): string =>
  process.env.REACT_APP_CONNECTOR_GATEWAY_URL || process.env.REACT_APP_PXW_CONNECTOR_GATEWAY_URL || '';

export const DEFAULT_GATEWAY_URL = getGatewayBaseUrlByEnv();

export const isConnectorComplete = (cfg: ConnectorConfig) =>
  Object.values(cfg).every(Boolean);

export const validateStep = (step: number, formData: PxwConnectorFormData): boolean => {
  const { tenant, vanilla, quickBooks, shipStation, diagnostic } = formData;
  switch (step) {
    case 0:
      return Boolean(tenant.tenantId && tenant.gatewayBaseUrl);
    case 1:
      return isConnectorComplete(vanilla.saas) && isConnectorComplete(vanilla.pxw);
    case 2:
      if (!tenant.configureQuickBooks) return true;
      return Boolean(
        quickBooks.qboClientId &&
        quickBooks.qboClientSecret &&
        quickBooks.realmId &&
        quickBooks.accessToken &&
        quickBooks.refreshToken &&
        quickBooks.purchaseOrderCharges &&
        quickBooks.manuallyConfigureSalesTax
      );
    case 3:
      if (!tenant.configureShipStation) return true;
      return Boolean(
        shipStation.storeNumber &&
        shipStation.webhookUrl &&
        shipStation.v1ApiKey &&
        shipStation.v1SecretKey &&
        shipStation.v2ProdKey
      );
    case 4: {
      if (!tenant.configureDiagnostic) return true;
      const { enableMannapovIce, enablePhoneCheck, mannapovIce, phoneCheck } = diagnostic;
      if (!enableMannapovIce && !enablePhoneCheck) return true;
      if (enableMannapovIce && (!mannapovIce.bearer || !mannapovIce.baseUrl || !mannapovIce.license)) return false;
      if (enablePhoneCheck && (!phoneCheck.apiKey || !phoneCheck.userName)) return false;
      return true;
    }
    default:
      return true;
  }
};

export const getTaskParams = (taskId: string, formData: PxwConnectorFormData) => {
  const rawGatewayUrl = formData.tenant.gatewayBaseUrl.replace(/\/+$/, '');
  const tenantId = formData.tenant.tenantId;

  let baseUrl = '';
  let relativePath = '';

  try {
    const parsedUrl = new URL(rawGatewayUrl);
    baseUrl = parsedUrl.origin;
    relativePath = parsedUrl.pathname.replace(/^\//, '');
  } catch {
    baseUrl = rawGatewayUrl;
    relativePath = '';
  }

  switch (taskId) {
    case 'vanilla':
      return { url: relativePath ? `${relativePath}/PxwTenantVanillaSettingSetup` : 'PxwTenantVanillaSettingSetup', baseUrl, payload: buildVanillaSettingsPayload(tenantId, formData.vanilla) };
    case 'qbo-configurator':
      return { url: relativePath ? `${relativePath}/PxwTenantErpConfigQboConfigurator` : 'PxwTenantErpConfigQboConfigurator', baseUrl, payload: buildQboConfiguratorPayload(tenantId, formData.quickBooks) };
    case 'qbo-setup':
      return { url: relativePath ? `${relativePath}/PxwTenantQboSetup` : 'PxwTenantQboSetup', baseUrl, payload: buildQboSetupPayload(tenantId, formData.quickBooks) };
    case 'shipstation':
      return { url: relativePath ? `${relativePath}/PxwTenantShipStationSetup` : 'PxwTenantShipStationSetup', baseUrl, payload: buildShipStationPayload(tenantId, formData.shipStation) };
    case 'diagnostic':
      return { url: relativePath ? `${relativePath}/PxwTenantProcessingSoftwareSetup` : 'PxwTenantProcessingSoftwareSetup', baseUrl, payload: buildDiagnosticPayload(tenantId, formData.diagnostic) };
    default:
      throw new Error(`Unknown task ${taskId}`);
  }
};
